import type { SanityItemKey } from "@marketing-sites/analytics/cms-analytics-id";
// =============================================================================
// Enums lifted from fin-ai app-local types for shared use.
// Originals: @/components/drlp/components/marquee/types, @/components/drlp/sections/pricing/types
// =============================================================================

export enum DrlpCompanyType {
  DEFAULT = "default",
  ECOMMERCE = "ecommerce",
  FINTECH = "fintech",
}

export enum PricingSectionType {
  GENERIC = "generic",
  STANDALONE = "standalone",
}

// =============================================================================
// Portable text (returned by minimalRichText fields)
// =============================================================================

export interface PortableTextSpan {
  _type: "span";
  text?: string;
  marks?: string[];
}

export interface PortableTextBlock {
  _type: string;
  children?: PortableTextSpan[];
}

// =============================================================================
// Core types
// =============================================================================

export interface DrlpPageMetadata {
  title: string;
  description: string;
  socialImage?: SanityImage;
  jsonLd?: string;
}

export interface DrlpCTA {
  _key?: SanityItemKey;
  variant?: "primary" | "secondary";
  themeClass?: string;
  text: string;
  linkType: "internal" | "external" | "formOverlay";
  externalRoute?: string;
  internalRoute?: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  form?: string;
  formOverlayTitle?: string;
  formOverlayDescription?: string;
  formOverlayImage?: SanityImage;
  formSuccessMessageTitle?: string;
  formSuccessMessageDescription?: string;
  formSubmitButtonText?: string;
  formPostSubmitAction?: "downloadPdf" | "redirect" | "showMessage";
  formSuccessRedirectUrl?: string;
  formPDF?: {
    asset?: {
      _id: string;
      url: string;
      originalFilename?: string;
    };
  };
}

export interface DrlpFeatureItem {
  _type?: "featureItem";
  _key?: string;
  text: string;
  hasGuaranteeDialog?: boolean;
}

export interface DrlpHeroTrustBar {
  _type: "sharedHeroTrustBar";
  _key?: string;
  // Resolved from the Global Stats singleton by the page query.
  finCustomers?: string | null;
  intercomCustomers?: string | null;
  resolutionRate?: string | null;
}

export type DrlpFeature = DrlpFeatureItem | DrlpHeroTrustBar;

export const isDrlpHeroTrustBar = (
  feature: DrlpFeature,
): feature is DrlpHeroTrustBar => feature._type === "sharedHeroTrustBar";

export interface DrlpImage {
  src: string;
  alt: string;
}

// =============================================================================
// Hero
// =============================================================================

export interface DrlpHeroVariation {
  label?: string;
  layout?: "centered" | "split";
  title: PortableTextBlock[];
  personalizedTitle?: PortableTextBlock[];
  description: string;
  hasCTAs: boolean;
  ctas: DrlpCTA[];
  hasFeatures: boolean;
  features: DrlpFeature[];
  hasMedia: boolean;
  mediaType: "animation" | "image" | "testimonial";
  // Key into the hero Rive library; resolved to a public .riv path per-app by
  // the renderer. Replaces the removed `riveAnimation` file upload.
  riveLibraryKey?: string;
  hideAnimationPlaceholder?: boolean;
  image?: SanityImage;
  // Optional mobile-specific foreground. Falls back to `image` when absent.
  mobileImage?: SanityImage;
  backgroundImage?: SanityImage;
  backgroundPreset?: string;
  // Dereferenced `testimonial` document, shown when mediaType is "testimonial".
  testimonial?: DrlpTestimonial;
  testimonialStyle?: "panel" | "card";
  hasStats?: boolean;
  stats?: { label: string; value: string }[];
  hasForm: boolean;
  marketoFormId?: string;
  formPostSubmitAction?: "showMessage" | "redirect" | "downloadPdf";
  formCTAText: string;
  formSuccessTitleText: string;
  formSuccessBodyText: string;
  formSuccessRedirectUrl?: string;
  formAsset?: {
    asset?: {
      _id: string;
      url: string;
      originalFilename?: string;
    };
  };
  riveUpload?: {
    asset?: {
      _id: string;
      url: string;
      originalFilename?: string;
    };
  };
}

export interface DrlpHeroSection extends DrlpHeroVariation {
  _type: "sharedHeroSection";
  enableAbTest?: boolean;
  abTestVariations?: DrlpHeroVariation[];
}

// =============================================================================
// Sanity image primitives
// =============================================================================

export interface SanityImageAsset {
  _id: string;
  url: string;
  metadata?: {
    lqip?: string;
    dimensions?: {
      width: number;
      height: number;
    };
  };
}

export interface SanityImage {
  asset?: SanityImageAsset;
  alt?: string;
}

// =============================================================================
// Body sections
// =============================================================================

export interface DrlpSlide {
  _key: SanityItemKey;
  media: {
    image: SanityImage;
  };
  title: string;
  description: string;
}

export interface DrlpFeaturesHighlightSection {
  _type: "sharedFeaturesHighlightSection";
  _key?: SanityItemKey;
  label: string;
  title: PortableTextBlock[];
  description?: string;
  enableCTAField?: boolean;
  ctas?: DrlpCTA[];
  // Opt-in: render slide media at the legacy 4:3 crop instead of the default 1:1.
  wideMedia?: boolean;
  slides: DrlpSlide[];
}

export interface DrlpTestimonial {
  _id: string;
  _key?: SanityItemKey;
  customer: {
    name: string;
    slug: { current: string };
    type: string;
    logo?: {
      asset: {
        _id: string;
        url: string;
      };
    };
  };
  quote: {
    _type: "highlightText";
    children: {
      _type: "span";
      marks: string[];
      text: string;
    }[];
  };
  highlight: string;
  name: string;
  jobTitle: string;
  // Optional author portrait — rendered as the testimonial image in the V2
  // (shared-component) section. Not fetched by the v1 carousel query.
  portrait?: SanityImage;
}

export interface DrlpTestimonialsSection {
  _type: "sharedTestimonialsSection";
  _key?: SanityItemKey;
  label: string;
  title: PortableTextBlock[];
  description?: string;
  enableCTAField?: boolean;
  ctas?: DrlpCTA[];
  testimonials: (DrlpTestimonial & { _key: SanityItemKey })[];
  hideLabels?: boolean;
}

// V2 renders via the shared @marketing-sites/ui/drlp DrlpTestimonials component: a
// section hero above one static card (1 up) or a company-tabbed carousel (2/3 up).
// Parallel test type — remove once v1 is cut over to the shared component.
export interface DrlpTestimonialsV2Section {
  _type: "sharedTestimonialsSectionV2";
  _key?: SanityItemKey;
  horizontalHeader?: boolean;
  label?: string;
  title?: PortableTextBlock[];
  description?: string;
  enableCTAField?: boolean;
  ctas?: DrlpCTA[];
  testimonials: (DrlpTestimonial & { _key: SanityItemKey })[];
  hideHeadshot?: boolean;
}

export interface DrlpPerformanceGraphsSection {
  _type: "performanceGraphsSection";
  label: string;
  title: PortableTextBlock[];
  description?: string;
  enableCTAField?: boolean;
  ctas?: DrlpCTA[];
  lineChartLabel: string;
  barChartLabel: string;
  barChartDescription?: string;
}

// V2 renders via the shared @marketing-sites/ui/drlp DrlpPerformanceGraphs
// component. Like the fin-local `performanceGraphsSection`, only the labels are
// authored; the chart data is hardcoded per-app in each registry's format fn so
// Fin and Intercom can show different numbers.
// Parallel test type — remove once a v1 is cut over.
export interface DrlpPerformanceGraphsV2Section {
  _type: "sharedPerformanceGraphsSectionV2";
  horizontalHeader?: boolean;
  label?: string;
  title: PortableTextBlock[];
  description?: string;
  enableCTAField?: boolean;
  ctas?: DrlpCTA[];
  lineChartLabel: string;
  lineChartDescription?: string;
  barChartLabel: string;
  barChartDescription?: string;
}

export interface DrlpImageTextColumnsSection {
  _type: "sharedImageTextColumnsSection";
  label: string;
  title: PortableTextBlock[];
  description?: string;
  enableCTAField?: boolean;
  ctas?: DrlpCTA[];
  items: {
    title: string;
    description: string;
    image: SanityImage;
    hasLink: boolean;
    link?: {
      text: string;
      href: string;
    };
  }[];
}

export interface DrlpComparisonTableSection {
  _type: "sharedComparisonTableSection";
  label?: string;
  title: PortableTextBlock[];
  description?: string;
  enableCTAField?: boolean;
  ctas?: DrlpCTA[];
  columnHeaders: {
    ourProduct: string;
    competitor: string;
  };
  comparisonRows: Array<{
    category: string;
    ourValue: string;
    competitorValue: string;
  }>;
}

// V2 renders via the shared @marketing-sites/ui/drlp DrlpComparisonTable component.
// Carries an optional section title + description (rendered above the table) plus
// the column headers + rows. Parallel test type — remove once v1 is cut over to
// the shared component.
export interface DrlpComparisonTableV2Section {
  _type: "sharedComparisonTableSectionV2";
  label?: string;
  title?: PortableTextBlock[];
  description?: string;
  enableCTAField?: boolean;
  ctas?: DrlpCTA[];
  columnHeaders: {
    ourProduct: string;
    competitor: string;
  };
  comparisonRows: Array<{
    category: string;
    ourValue: string;
    competitorValue: string;
  }>;
}

export interface DrlpTextColumnsWithGraphOrImageSection {
  _type: "sharedTextColumnsWithGraphOrImageSection";
  label: string;
  title: PortableTextBlock[];
  description?: string;
  enableCTAField?: boolean;
  ctas?: DrlpCTA[];
  features: {
    label: string;
    title: string;
    primaryContent: string;
    secondaryContent: string;
  }[];
  images: {
    hasImages: boolean;
    desktop: SanityImage;
    mobile: SanityImage;
  };
  barChart: {
    hasBarChart: boolean;
    label: string;
    description: string;
  };
  lineGraph: {
    hasLineGraph: boolean;
    label: string;
  };
}

export interface DrlpStatsSection {
  _type: "sharedStatsSection";
  label?: string;
  title?: PortableTextBlock[];
  description?: string;
  enableCTAField?: boolean;
  ctas?: DrlpCTA[];
  stats: {
    label: string;
    value: string;
    description: string;
  }[];
}

export interface DrlpStatisticsV2Section {
  _type: "sharedStatisticsV2Section";
  horizontalHeader?: boolean;
  label?: string;
  title?: PortableTextBlock[];
  description?: string;
  stats: {
    label: string;
    value: string;
    description: string;
  }[];
}

export interface DrlpCsatBenchmarkSection {
  _type: "sharedCsatBenchmarkSection";
  horizontalHeader?: boolean;
  label?: string;
  title?: PortableTextBlock[];
  description?: string;
  enableCTAField?: boolean;
  ctas?: DrlpCTA[];
}

export interface DrlpRoiCalculatorSection {
  _type: "sharedRoiCalculatorSection";
  _key?: SanityItemKey;
  horizontalHeader?: boolean;
  label?: string;
  title?: PortableTextBlock[];
  description?: string;
  defaultInputs?: {
    conversationsPerMonth?: number;
    supportAgents?: number;
    annualCostPerAgent?: number;
  };
  enableCTAField?: boolean;
  ctas?: DrlpCTA[];
}

export interface DrlpSpeaker {
  _key: SanityItemKey;
  name: string;
  jobTitle: string;
  company?: string;
  linkedinUrl?: string;
  image?: SanityImage & { hotspot?: { x: number; y: number } };
}

export interface DrlpSpeakersSection {
  _type: "sharedSpeakersSection";
  _key?: SanityItemKey;
  horizontalHeader?: boolean;
  label?: string;
  title?: PortableTextBlock[];
  description?: string;
  speakers?: DrlpSpeaker[];
  enableCTAField?: boolean;
  ctas?: DrlpCTA[];
}

export interface DrlpBenefitCardsSection {
  _type: "sharedBenefitCardsSection";
  horizontalHeader?: boolean;
  label?: string;
  title?: PortableTextBlock[];
  description?: string;
  enableCTAField?: boolean;
  ctas?: DrlpCTA[];
  cards: {
    title: string;
    description: string;
  }[];
}

export interface DrlpProductFeaturesSection {
  _type: "sharedProductFeaturesSection";
  _key?: SanityItemKey;
  horizontalHeader?: boolean;
  label?: string;
  title?: PortableTextBlock[];
  description?: string;
  enableCTAField?: boolean;
  ctas?: DrlpCTA[];
  layout?: "3-up" | "3-up-no-image" | "single-image";
  image?: SanityImage;
  cards: {
    _key: SanityItemKey;
    title: string;
    description: string;
    image?: SanityImage;
  }[];
}

export interface DrlpFaqSection {
  _type: "sharedFaqSection";
  label?: string;
  title?: PortableTextBlock[];
  faqs: {
    _key?: SanityItemKey;
    question: string;
    answer: string;
  }[];
}

export interface DrlpCtaBannerBandSection {
  _type: "sharedCtaBannerBandSection";
  label?: string;
  title?: PortableTextBlock[];
  description?: string;
  ctas?: DrlpCTA[];
}

// Fin First Demo intro/CTA section — label/headline/description/CTAs only. Fin-only.
export interface DrlpFinDemoSection {
  _type: "sharedFinDemoSection";
  label?: string;
  title?: PortableTextBlock[];
  description?: string;
  ctas?: DrlpCTA[];
}

// Integrations band — the orbit illustration is baked into the DrlpFinIntegrations
// component; copy and the CTA are editable. Every field is optional: it is absent
// on pages authored before the field existed, in which case the component falls
// back to its built-in default.
export interface DrlpFinIntegrationsSection {
  _type: "sharedFinIntegrationsSection";
  label?: string;
  title?: string;
  description?: string;
  items?: string[];
  illustrationAlt?: string;
  ctas?: DrlpCTA[];
}

// Helpdesk band — the illustration is baked into the DrlpHelpdeskIntegrations
// component; copy and the CTA are editable. Every field is optional: it is absent
// on pages authored before the field existed, in which case the component falls
// back to its built-in default.
export interface DrlpHelpdeskIntegrationsSection {
  _type: "sharedHelpdeskIntegrationsSection";
  label?: string;
  title?: string;
  description?: string;
  items?: string[];
  illustrationAlt?: string;
  ctas?: DrlpCTA[];
}

// Fin-only "Fin Voice" section — intro + interactive "Talk to Fin about Fin"
// orb demo + an explanatory card. Rendered by the fin-ai-local DrlpVoice
// component (the voice call modal lives in the fin app), not a shared component.
export interface DrlpVoiceV2Section {
  _type: "sharedVoiceSectionV2";
  label?: string;
  title?: PortableTextBlock[];
  description?: string;
  cardTitle?: string;
  cardBody?: string;
  ctas?: DrlpCTA[];
}

// The "Video" section — intro (label/title/description) above an inline video
// player. The video is a reference to a `sharedVideo` library document,
// dereferenced by the query to its link/title. Fin-only.
export interface DrlpVideoV2Section {
  _type: "sharedVideoSectionV2";
  label?: string;
  title?: PortableTextBlock[];
  description?: string;
  // Dereferenced `sharedVideo` document; null if the reference is unset/unpublished.
  video?: {
    videoSrc?: string;
    videoTitle?: string;
    title?: string;
  } | null;
}

export interface DrlpFooterCtaVariation {
  showFooterCta?: boolean;
  label?: string;
  title: PortableTextBlock[];
  ctas?: DrlpCTA[];
  theme: "light" | "dark";
  image?: SanityImage;
}

export interface DrlpFooterCtaSection extends DrlpFooterCtaVariation {
  _type: "sharedCtaBannerSection";
  enableAbTest?: boolean;
  abTestVariations?: DrlpFooterCtaVariation[];
}

export interface DrlpCalloutCardSection {
  _type: "sharedCalloutCardSection";
  title: PortableTextBlock[];
  description: string;
  ctas: DrlpCTA[];
}

export interface DrlpFeatureSpotlightSection {
  _type: "sharedFeatureSpotlightSection";
  _key?: SanityItemKey;
  label: string;
  title: PortableTextBlock[];
  description?: string;
  enableCTAField?: boolean;
  ctas?: DrlpCTA[];
  mediaPosition?: "vertical" | "horizontal" | "horizontalRtl";
  mediaType: "finVoice" | "animation" | "image";
  // Key into DRLP_RIVE_LIBRARY; resolved to a public .riv path per-app by the
  // renderer. Replaces the removed `animation` file upload.
  riveLibraryKey?: string;
  image?: {
    asset?: {
      _id: string;
      url: string;
      metadata?: {
        lqip?: string;
        dimensions?: {
          width: number;
          height: number;
        };
      };
    };
    alt: string;
  };
  cards?: {
    title: string;
    description: string;
  }[];
}

export interface DrlpAbTestSection {
  _type: "sharedAbTestSection" | "finAbTestSection";
  testName: string;
  enabled: boolean;
  control: DrlpSection[];
  variations?: DrlpSection[];
  previewMode?: string;
  notes?: string;
}

// =============================================================================
// Fixed sections (specific positions)
// =============================================================================

export interface DrlpHighlight {
  text: string;
  hasGuaranteeDialog?: boolean;
}

export interface DrlpMarqueeVariation {
  heading?: string | null;
  showCompanyLogos: boolean;
  companyType: DrlpCompanyType;
  layout?: "wall" | "banner";
}

export interface DrlpMarqueeSection extends DrlpMarqueeVariation {
  _type: "sharedMarqueeSection";
  enableAbTest?: boolean;
  abTestVariations?: DrlpMarqueeVariation[];
}

export interface DrlpNavigationCta {
  _key?: SanityItemKey;
  text: string;
  linkType: "internal" | "external";
  externalRoute?: string;
  internalRoute?: string;
}

export interface DrlpNavigationVariation {
  enableCustomNavigation: boolean;
  enableLogoLink?: boolean;
  ctas?: DrlpNavigationCta[];
}

export interface DrlpNavigationGroup extends DrlpNavigationVariation {
  enableAbTest?: boolean;
  abTestVariations?: DrlpNavigationVariation[];
}

export interface DrlpPricingVariation {
  showPricing?: boolean;
  variant: PricingSectionType;
}

export interface DrlpPricingSection extends DrlpPricingVariation {
  enableAbTest?: boolean;
  abTestVariations?: DrlpPricingVariation[];
}

export interface DrlpIntegrationsVariation {
  showIntegrations: boolean;
  enableCTAField: boolean;
  ctas?: DrlpCTA[];
}

export interface DrlpIntegrationsSection extends DrlpIntegrationsVariation {
  _type: "integrationsSection";
  enableAbTest?: boolean;
  abTestVariations?: DrlpIntegrationsVariation[];
}

// =============================================================================
// Carousel section
// =============================================================================

export interface DrlpCarouselSlide {
  _key?: SanityItemKey;
  title: string;
  description: string;
  linkHref?: string;
  smallImage: SanityImage;
  largeImage: SanityImage;
  slideBackdrop?: SanityImage;
  slideBackdropPreset?: string;
}

export interface DrlpCarouselSection {
  _type: "sharedCarouselSection";
  label: string;
  title: PortableTextBlock[];
  description?: string;
  enableCTAField?: boolean;
  ctas?: DrlpCTA[];
  orientation?: "horizontal" | "horizontalRtl" | "vertical";
  slides: DrlpCarouselSlide[];
}

// V2 renders via the shared @marketing-sites/ui/drlp DrlpFeatureCarousel component;
// same data shape as DrlpCarouselSection. Parallel test type — remove once v1 is
// cut over.
export interface DrlpCarouselV2Section {
  _type: "sharedCarouselSectionV2";
  horizontalHeader?: boolean;
  label: string;
  title: PortableTextBlock[];
  description?: string;
  enableCTAField?: boolean;
  ctas?: DrlpCTA[];
  orientation?: "horizontal" | "horizontalRtl" | "vertical";
  slides: DrlpCarouselSlide[];
}

export interface DrlpG2Badge {
  _id: string;
  name: string;
  badge?: {
    asset?: {
      _id: string;
      url: string;
    };
  };
}

export interface DrlpG2BadgesSection {
  _type: "sharedG2BadgesSection";
  title: PortableTextBlock[];
  badges: DrlpG2Badge[];
}

// V2 renders via the shared @marketing-sites/ui/drlp component; same data shape
// as DrlpG2BadgesSection. Parallel test type — remove once v1 is cut over.
export interface DrlpG2BadgesV2Section {
  _type: "sharedG2BadgesSectionV2";
  title: PortableTextBlock[];
  badges: DrlpG2Badge[];
}

export type DrlpPricingLocale = "en-US" | "de-DE" | "fr-FR" | "pt-BR" | "ja-JP";

// V2 of the Pricing section, rendered via the shared @marketing-sites/ui/drlp
// DrlpPricing component. Like the fin-local pricing section, this is
// fixed-copy: title, notes, cards, prices, tooltips and CTAs are all
// hardcoded per-app in each registry's format fn, keyed by `locale` — nothing
// is authored in Sanity. Parallel test type — remove once the fin v1 is cut over.
export interface DrlpPricingV2Section {
  _type: "sharedPricingSectionV2";
  locale?: DrlpPricingLocale;
}

// =============================================================================
// Union types
// =============================================================================

export type DrlpSection =
  | DrlpFeaturesHighlightSection
  | DrlpTestimonialsSection
  | DrlpTestimonialsV2Section
  | DrlpPerformanceGraphsSection
  | DrlpPerformanceGraphsV2Section
  | DrlpImageTextColumnsSection
  | DrlpComparisonTableSection
  | DrlpComparisonTableV2Section
  | DrlpCalloutCardSection
  | DrlpTextColumnsWithGraphOrImageSection
  | DrlpStatsSection
  | DrlpStatisticsV2Section
  | DrlpCsatBenchmarkSection
  | DrlpRoiCalculatorSection
  | DrlpSpeakersSection
  | DrlpVoiceV2Section
  | DrlpVideoV2Section
  | DrlpBenefitCardsSection
  | DrlpProductFeaturesSection
  | DrlpFaqSection
  | DrlpCtaBannerBandSection
  | DrlpFinDemoSection
  | DrlpFinIntegrationsSection
  | DrlpHelpdeskIntegrationsSection
  | DrlpPricingV2Section
  | DrlpFeatureSpotlightSection
  | DrlpCarouselSection
  | DrlpCarouselV2Section
  | DrlpG2BadgesSection
  | DrlpG2BadgesV2Section
  | DrlpAbTestSection;

// =============================================================================
// Page root
// =============================================================================

export interface DrlpPage {
  _id: string;
  slug: {
    current: string;
  };
  isLive?: boolean;
  site?: string;
  pageMetadata: DrlpPageMetadata;
  enablePersonalization?: boolean;
  experimentFlagKey?: string;
  heroSection: DrlpHeroSection;
  marqueeSection: DrlpMarqueeSection;
  footerCtaSection?: DrlpFooterCtaSection;
  navigationGroup?: DrlpNavigationGroup;
  integrationsSection?: DrlpIntegrationsSection;
  pricingSection?: DrlpPricingSection;
  sections: DrlpSection[];
}
